import { lazy, type LazyExoticComponent } from "react";
import { MARKETING_PAGES } from "@workspace/strings";

/**
 * The public marketing route table.
 *
 * Pairs each path in `MARKETING_PAGES` (lib/strings/src/en/meta.ts — the single
 * source of truth that also drives the prerenderer and sitemap) with its lazy
 * component. Split out of App.tsx so the pages stay code-split away from the
 * operator bundle, and so route/metadata parity is testable on its own
 * (routes.test.ts).
 *
 * Paths NOT listed here fall through to `AuthenticatedApp`, which bounces
 * signed-out visitors to /sign-in — so a page with metadata but no route entry
 * is invisible to visitors while still being advertised in the sitemap. The
 * parity test exists to catch exactly that.
 *
 * `/` and the legal pages are wired directly in App.tsx (they predate this
 * table and have their own guards), so they're excluded here.
 */
type PageComponent = LazyExoticComponent<() => React.JSX.Element>;

/** Paths owned by App.tsx directly rather than this table. */
export const APP_OWNED_PATHS = new Set([
  "/",
  "/privacy",
  "/terms",
  "/cookies",
  "/delete-account",
]);

export const MARKETING_ROUTE_COMPONENTS: Record<string, PageComponent> = {
  "/features": lazy(() => import("@/pages/marketing/features")),
  "/apps": lazy(() => import("@/pages/marketing/apps")),
  "/compare": lazy(() => import("@/pages/marketing/compare")),
};

/** Every marketing path this table is responsible for rendering. */
export const MARKETING_ROUTE_PATHS = Object.keys(MARKETING_ROUTE_COMPONENTS);

/** Paths declared in metadata that this table (or App.tsx) must cover. */
export const DECLARED_MARKETING_PATHS = MARKETING_PAGES.map((p) => p.path);
